import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { Loader2 } from "lucide-react";
import DOMPurify from "dompurify";

export default function PublicCanvasViewer() {
  const { shareToken } = useParams<{ shareToken: string }>();

  // Use the public query we created in canvases.ts
  const canvas = useQuery(api.canvases.getPublicCanvas, shareToken ? { shareToken } : "skip");

  if (canvas === undefined) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-slate-900">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (canvas === null) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center bg-slate-900 text-slate-200">
        <h1 className="mb-2 text-2xl font-bold">Document Not Found</h1>
        <p className="text-slate-400">This link may be invalid or has expired.</p>
      </div>
    );
  }

  // Sanitize the HTML Content
  const safeHtmlContent = DOMPurify.sanitize(canvas.htmlContent, {
    ALLOWED_TAGS: [
      "div", "span", "p", "h1", "h2", "h3", "h4", "h5", "h6",
      "ul", "ol", "li", "table", "thead", "tbody", "tr", "th", "td",
      "a", "button", "img", "svg", "path", "g",
      "header", "footer", "section", "article", "aside", "main", "nav",
      "figure", "figcaption", "strong", "b", "em", "i", "br", "hr", "style"
    ],
    ALLOW_DATA_ATTR: true,
    ADD_ATTR: ["style", "class"],
    FORBID_TAGS: ["script", "iframe", "object", "embed", "form"],
    FORBID_ATTR: [
      "onerror", "onload", "onclick", "onmouseover",
      "onkeydown", "onkeyup", "onsubmit"
    ],
  });

  const baseTemplate = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <script src="https://cdn.tailwindcss.com"></script>
      <script>
        tailwind.config = {
          theme: {
            extend: {
              colors: {
                brand: {
                  50: '#f0f9ff',
                  100: '#e0f2fe',
                  500: '#0ea5e9',
                  600: '#0284c7',
                  900: '#0c4a6e',
                }
              }
            }
          }
        }
      </script>
      <style>
        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          background-color: #f8fafc; /* light slate */
          margin: 0;
          padding: 1.5rem;
          color: #1f2937;
        }
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        ::-webkit-scrollbar-track {
          background: transparent; 
        }
        ::-webkit-scrollbar-thumb {
          background: #cbd5e1; 
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #94a3b8; 
        }
        /* Disable actions for public viewer */
        button[data-action] {
            pointer-events: none;
            opacity: 0.7;
        }
      </style>
    </head>
    <body class="antialiased">
      <div class="max-w-5xl mx-auto bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden min-h-[calc(100vh-3rem)]">
        ${safeHtmlContent}
      </div>
    </body>
    </html>
  `;

  return (
    <div className="flex h-screen w-full flex-col bg-slate-900">
      {/* Header bar */}
      <div className="flex items-center justify-between border-b border-slate-800 bg-slate-900 px-6 py-4">
        <h1 className="text-lg font-semibold text-slate-100">{canvas.title}</h1>
        <div className="text-sm text-slate-400">
          Created on {new Date(canvas.createdAt).toLocaleDateString()}
        </div>
      </div>
      
      {/* Iframe content */}
      <div className="flex-1 overflow-hidden bg-slate-800">
        <iframe
          srcDoc={baseTemplate}
          className="h-full w-full border-0"
          sandbox="allow-scripts"
          referrerPolicy="no-referrer"
          loading="lazy"
          title={canvas.title}
        />
      </div>
    </div>
  );
}
